/**
 * Translation for the guest site.
 *
 * The rules are the ones `docs/decisions.md` §8 sets and the dashboard already
 * follows: English is the SOURCE, Dutch is a complete catalogue the compiler
 * enforces, Dutch is the runtime default, and an English session never fetches
 * the Dutch chunk. No Dutch literal appears in any `.tsx` here.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A COPY OF THE DASHBOARD'S FILE AND NOT AN IMPORT
 * ---------------------------------------------------------------------------
 *
 * The two apps share the RULES, not the catalogue, and the catalogue is what
 * the type is built out of — `MessageKey` here is the guest site's key set and
 * has to be, or a guest string would be checkable only by adding it to the
 * operator's catalogue too. Sharing the fifty lines of lookup machinery to make
 * two unrelated key sets one type is a worse trade than typing it twice.
 *
 * The availability code is the opposite case and is imported, not copied,
 * because there correctness depends on the two answers being identical.
 *
 * ---------------------------------------------------------------------------
 * WHY THE LANGUAGE IS CONTROLLED FROM OUTSIDE
 * ---------------------------------------------------------------------------
 *
 * The dashboard's provider owns the choice and persists it, which is right for
 * an application. A widget must not: the WordPress page it is embedded in
 * already knows what language it is being read in, and a widget that overrode
 * that from its own `localStorage` would show Dutch on an English page for
 * anyone who had once switched. So the provider takes `lang` and `setLang` and
 * the host decides where they come from — the plugin's `locale` option in one
 * case, a stored preference in the other.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { en, type Catalogue, type MessageKey } from '../locales/en';
import { browserLanguages, readChoice, resolveLanguage, writeChoice } from '@shared/index';

export type { Catalogue, MessageKey } from '../locales/en';

export type Lang = 'nl' | 'en';

export type Values = Record<string, string | number>;

/**
 * How each language arrives. English is already here — it is the source and the
 * type every other catalogue is checked against. The rest are `import()` calls,
 * which is what keeps them out of an English bundle.
 */
const LOADERS: Record<Lang, () => Promise<Catalogue>> = {
  en: () => Promise.resolve(en),
  nl: () => import('../locales/nl').then((m) => m.nl),
};

const loaded = new Map<Lang, Catalogue>([['en', en]]);

/**
 * Language names in their own language, never translated. A switcher exists for
 * someone who cannot read what is currently on screen, and "Dutch" is no use to
 * the person who needs it.
 */
export const LANGUAGES: ReadonlyArray<readonly [Lang, string]> = [
  ['nl', 'Nederlands'],
  ['en', 'English'],
];

/**
 * The floor, not the default.
 *
 * This was `nl`, on the reasoning that the first park is Dutch and so are its
 * guests. Mostly true and the wrong shape: a German family reading the site in
 * German got Dutch, and the park's own site is not the only host — the same widget
 * runs inside an English WordPress page. So the language is RESOLVED now, and this
 * is only what a browser asking for something unsupported falls back to.
 *
 * English, because it is the source language: a reader meeting it is meeting the
 * original rather than a translation of unknown age.
 */
export const DEFAULT_LANG: Lang = 'en';
const SUPPORTED: readonly Lang[] = ['en', 'nl'];

export function isLang(v: unknown): v is Lang {
  return v === 'nl' || v === 'en';
}

export type TFunction = (key: MessageKey, values?: Values) => string;

interface I18nApi {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: TFunction;
  /**
   * The BCP 47 tag for `Intl`. Distinct from `lang` because a country name or a
   * price wants a full locale and the catalogue key set does not — and because
   * a park in Flanders would one day want `nl-BE` money with the same Dutch
   * words.
   */
  locale: string;
}

const LOCALES: Record<Lang, string> = { nl: 'nl-NL', en: 'en-GB' };

const I18nContext = createContext<I18nApi | null>(null);

/**
 * `{name}` substitution. An unknown placeholder is left standing rather than
 * replaced with the word `undefined`: a visible `{count}` is a bug report and
 * "undefined" mid-sentence is a mystery.
 */
export function interpolate(text: string, values?: Values): string {
  if (!values) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => (
    name in values ? String(values[name]) : whole
  ));
}

export function translate(cat: Catalogue, key: MessageKey, values?: Values): string {
  // The one-directional net. `Catalogue` makes a missing Dutch key a compile
  // error, so this branch is unreachable from a catalogue that type-checked; it
  // exists so one absent key degrades to an English word instead of throwing a
  // booking form away, and it can only run in that direction — English IS `en`.
  const message = cat[key] ?? en[key];
  // A key in NEITHER catalogue renders as itself rather than throwing. Unreachable
  // through the type system and reachable at runtime — a key built from data, a
  // chunk from an older build — and `message.one` on undefined took a booking form
  // down over one label. The key is visible, names the fault, and is greppable.
  if (message === undefined || message === null) return key;
  const text = typeof message === 'string'
    ? message
    : (Number(values?.count) === 1 ? message.one : message.other);
  return interpolate(text, values);
}

export function I18nProvider({ lang, setLang, children }: {
  lang: Lang;
  setLang: (lang: Lang) => void;
  children: ReactNode;
}) {
  const [, bump] = useState(0);

  useEffect(() => {
    if (loaded.has(lang)) return undefined;
    let cancelled = false;
    void LOADERS[lang]().then((cat) => {
      loaded.set(lang, cat);
      if (!cancelled) bump((n) => n + 1);
    });
    return () => { cancelled = true; };
  }, [lang]);

  // Read through the cache during render rather than mirroring it into state: a
  // state copy lags one render behind, and that render is a frame of the WRONG
  // language on screen — the English-shows-through that the absence of a
  // fallback exists to rule out.
  const catalogue = loaded.get(lang) ?? null;

  const api = useMemo<I18nApi | null>(() => (catalogue ? {
    lang,
    setLang,
    locale: LOCALES[lang],
    t: (key, values) => translate(catalogue, key, values),
  } : null), [catalogue, lang, setLang]);

  // One tick, on the first switch to a language whose chunk is still in flight.
  // Nothing is a better answer than the previous language's words.
  if (!api) return null;

  return <I18nContext.Provider value={api}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nApi {
  const api = useContext(I18nContext);
  if (!api) throw new Error('useI18n outside an I18nProvider');
  return api;
}

export function useT(): TFunction {
  return useI18n().t;
}

/**
 * Language state for a host that owns its page.
 *
 * The widget does NOT use this — its language comes from the plugin. This is
 * the app's version: remember what the guest chose, because someone who
 * switched to English on the search screen should not be handed a Dutch
 * enquiry form.
 */
export function useStoredLang(initial?: Lang): [Lang, (l: Lang) => void] {
  // `initial` is the HOST speaking — a `?lang=` in the URL — and it wins on
  // ARRIVAL, including over a choice made on a previous visit. A link somebody
  // was sent in English opens in English.
  const [lang, set] = useState<Lang>(() => resolveLanguage<Lang>({
    supported: SUPPORTED,
    fallback: DEFAULT_LANG,
    host: initial,
    chosen: readChoice<Lang>(LANG_KEY, SUPPORTED),
    browser: browserLanguages(),
  }));

  /**
   * The dropdown on the page beats everything, including the URL that opened it.
   *
   * Arriving on `?lang=en`, switching to Dutch and then reloading used to hand the
   * guest English again — the URL still said so, and the URL outranks a stored
   * choice. Reasonable rules, absurd result: the guest is looking at the control
   * they just used and it has been overruled by a link they did not write.
   *
   * Rewriting the parameter is better than adding a precedence rule for it,
   * because it removes the disagreement rather than deciding it. The address bar
   * then matches the page in every case, so the URL a guest copies and sends to
   * somebody else is in the language they were reading — which is what they meant
   * by sending it.
   *
   * `replaceState`, not `pushState`: choosing a language is not a place, and
   * making Back undo it would trap a guest who used it twice.
   */
  const setLang = useCallback((next: Lang) => {
    set(next);
    writeChoice(LANG_KEY, next);
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('lang') === next) return;
      url.searchParams.set('lang', next);
      window.history.replaceState(window.history.state, '', url.toString());
    } catch {
      // A URL this cannot parse is not worth losing a language change over.
    }
  }, []);
  return [lang, setLang];
}

const LANG_KEY = 'pms.guest.lang';

/**
 * A message whose emphasis is part of the sentence.
 *
 * The marker travels inside the message — `*like this*` — so a translator can
 * move it to the word that carries the emphasis in their language, rather than
 * being handed two halves of a sentence and no way to rearrange them.
 */
export function Rich({ id, values, mark = 'strong' }: {
  id: MessageKey;
  values?: Values;
  mark?: 'strong' | 'em';
}) {
  const t = useT();
  const Mark = mark;
  return (
    <>
      {t(id, values).split(/\*([^*]+)\*/).map((part, i) => (
        i % 2 === 0 ? part : <Mark key={`${i}-${part}`}>{part}</Mark>
      ))}
    </>
  );
}
